import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import { MemoizedLayoutBlockRenderer } from '@unifyapps/carbon/no-code/components/MemoizedLayoutBlockRenderer';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import { getBlockKey } from '@unifyapps/carbon/no-code/utils/block';
import type { StackBlockStateType, StackComponentType } from '@unifyapps/defs/blocks/Stack/types';
import { UA_INTERFACE_ENABLE_HOVER_EFFECT } from '@unifyapps/style/themes/consts';
import { default as StackPrimitive } from '@unifyapps/ui/_components/Stack';
import { clsx } from 'clsx';
import { useMemo } from 'react';
import { useBlockInteractions } from '../hooks/useBlockInteractions';
import { useStackBaseStyles } from './useStackBaseStyles';
import { getDirection } from './utils';

export type StackProps = BlockComponentProps<
  StackComponentType,
  StackBlockStateType,
  StackBlockStateType,
  HTMLElement
>;

// Effect-free Stack — byte-identical to the pre-effects render path so a Stack
// without any `appearance.effects` pays zero overhead from the effects system.
export function BaseStack({
  blockState,
  attributesAndEventListeners,
  computedBlockState,
  events,
  registry,
  dndRef,
  block,
}: StackProps) {
  const { content, appearance } = computedBlockState;

  const { className, wrapClassName, finalStyle, blurStyle, linearGradientStyle, BlockRenderer } =
    useStackBaseStyles({ appearance, block, registry });

  const mergedStyle = useMemo(
    () => ({ ...finalStyle, ...blurStyle, ...linearGradientStyle }),
    [finalStyle, blurStyle, linearGradientStyle],
  );

  const { emitEvent } = useBlockEvents(events, computedBlockState.id);
  const { onClick, onMouseEnter, longPressProps } = useBlockInteractions({
    events,
    emitEvent,
    blockType: 'Stack',
  });

  return (
    <StackPrimitive
      alignItems={appearance?.alignItems}
      className={clsx(className, wrapClassName, {
        [`${UA_INTERFACE_ENABLE_HOVER_EFFECT} cursor-pointer`]: Boolean(onClick),
      })}
      direction={getDirection(appearance)}
      justifyContent={appearance?.justifyContent}
      ref={dndRef}
      style={mergedStyle}
      {...attributesAndEventListeners}
      {...longPressProps}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      {content.blockIds?.map((blockId, index) => (
        <MemoizedLayoutBlockRenderer
          BlockRenderer={BlockRenderer}
          blockId={blockId}
          index={index}
          // eslint-disable-next-line react/no-array-index-key
          key={getBlockKey(blockId, index)}
          parentBlockId={blockState.id}
        />
      ))}
    </StackPrimitive>
  );
}
